import { memo, useCallback, useMemo, type ReactNode } from 'react';
import { Button } from '@mui/material';

export interface SelectionOption {
    readonly label: string;
    readonly value: any;
    readonly disabled?: boolean;
}

export interface SelectionItemGroupProps {
    readonly options: readonly SelectionOption[];
    readonly value: any | readonly any[];
    readonly onChange: (value: any | readonly any[]) => void;
    readonly size?: 'small' | 'medium' | 'large';
    readonly fullWidth?: boolean;
    readonly className?: string;
    readonly multiple?: boolean;
    readonly disabled?: boolean;
}

interface SelectionItemProps {
    readonly selected: boolean;
    readonly label: string;
    readonly size?: 'small' | 'medium' | 'large';
    readonly disabled: boolean;
    readonly onClick: () => void;
    readonly multiple: boolean;
    readonly hasAdjacentPrev: boolean;
    readonly hasAdjacentNext: boolean;
}
function SelectionItemBase({
    selected,
    label,
    size = 'small',
    disabled,
    onClick,
    multiple,
    hasAdjacentPrev,
    hasAdjacentNext
}: SelectionItemProps) {
    const className = [
        'selection-item',
        selected ? 'selected' : '',
        selected && multiple && hasAdjacentPrev ? 'adjacent-prev' : '',
        selected && multiple && hasAdjacentNext ? 'adjacent-next' : ''
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <Button
            className={className}
            role={multiple ? 'checkbox' : 'radio'}
            aria-checked={selected}
            disabled={disabled}
            onClick={onClick}
            size={size}
            disableRipple
        >
            {label}
        </Button>
    );
}

const SelectionItem = memo(SelectionItemBase);
SelectionItem.displayName = 'SelectionItem';

function renderMultipleThumbs(selectedIndices: number[], totalCount: number): ReactNode {
    if (selectedIndices.length === 0) return null;

    const sorted = [...selectedIndices].sort((a, b) => a - b);
    const groups: Array<{ start: number; count: number }> = [];
    let currentStart = sorted[0];
    let currentCount = 1;

    for (let i = 1; i < sorted.length; i++) {
        if (sorted[i] === sorted[i - 1] + 1) {
            currentCount++;
        } else {
            groups.push({ start: currentStart, count: currentCount });
            currentStart = sorted[i];
            currentCount = 1;
        }
    }
    groups.push({ start: currentStart, count: currentCount });

    return groups.map((group) => (
        <div
            key={`thumb-${group.start}-${group.count}`}
            className="selection-item-group__thumb"
            style={{
                width: `${(100 * group.count) / totalCount}%`,
                left: `${(group.start * 100) / totalCount}%`,
                transform: 'none'
            }}
            aria-hidden="true"
        />
    ));
}

function SelectionItemGroupBase({
    options,
    value,
    onChange,
    size = 'small',
    fullWidth = false,
    className = '',
    multiple = false,
    disabled = false
}: SelectionItemGroupProps) {
    const selectedIndices = useMemo<number[]>(() => {
        if (multiple) {
            const values: readonly any[] = Array.isArray(value) ? value : [];
            return options.reduce<number[]>((acc, opt, idx) => {
                if (values.includes(opt.value)) acc.push(idx);
                return acc;
            }, []);
        }
        const idx = options.findIndex((o) => o.value === value);
        return idx >= 0 ? [idx] : [];
    }, [options, value, multiple]);

    const selectedSet = useMemo(() => new Set(selectedIndices), [selectedIndices]);

    const handleClick = useCallback(
        (optionValue: any) => {
            if (multiple) {
                const values: any[] = Array.isArray(value) ? [...value] : [];
                const idx = values.indexOf(optionValue);
                if (idx >= 0) {
                    values.splice(idx, 1);
                } else {
                    values.push(optionValue);
                }
                onChange(values);
            } else {
                onChange(optionValue);
            }
        },
        [multiple, value, onChange]
    );

    const containerClass = [
        'selection-item-group',
        multiple ? 'multiple-mode' : '',
        fullWidth ? 'full-width' : '',
        className
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div
            className={containerClass}
            role="radiogroup"
            aria-label={multiple ? 'Multiple selection' : 'Single selection'}
            aria-disabled={disabled || undefined}
        >
            {multiple
                ? renderMultipleThumbs(selectedIndices, options.length)
                : selectedIndices.length > 0 && (
                      <div
                          className="selection-item-group__thumb"
                          style={{
                              width: `${100 / options.length}%`,
                              transform: `translateX(${selectedIndices[0] * 100}%)`
                          }}
                          aria-hidden="true"
                      />
                  )}

            {options.map((option, index) => {
                const selected = multiple ? selectedSet.has(index) : option.value === value;

                return (
                    <SelectionItem
                        key={String(option.value)}
                        selected={selected}
                        label={option.label}
                        size={size}
                        disabled={disabled || (option.disabled ?? false)}
                        onClick={() => handleClick(option.value)}
                        multiple={multiple}
                        hasAdjacentPrev={selected && selectedSet.has(index - 1)}
                        hasAdjacentNext={selected && selectedSet.has(index + 1)}
                    />
                );
            })}
        </div>
    );
}

const SelectionItemGroup = memo(SelectionItemGroupBase);
SelectionItemGroup.displayName = 'SelectionItemGroup';

export default SelectionItemGroup;
